import React, { useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Bold, Italic, Underline, List, ListOrdered } from "lucide-react";
import { cn } from "../../lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = "Enter your text here...",
  disabled = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const currentContent = editorRef.current.innerHTML || "";
      if (currentContent !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value]);

  const execCommand = (command: string) => {
    if (disabled) return;
    document.execCommand(command, false);
    editorRef.current?.focus();
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label className="text-sm font-medium text-white">{label}</Label>
      )}
      <div className="p-4 border border-white/10 rounded-lg bg-black/20">
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 p-2 border border-white/10 rounded bg-black/40">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand("bold")}
              disabled={disabled}
              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand("italic")}
              disabled={disabled}
              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand("underline")}
              disabled={disabled}
              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Underline className="h-4 w-4" />
            </Button>

            <div className="w-px h-6 bg-white/20 mx-1" />

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand("insertUnorderedList")}
              disabled={disabled}
              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand("insertOrderedList")}
              disabled={disabled}
              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
          </div>

          {/* Editor */}
          <div
            ref={editorRef}
            contentEditable={!disabled}
            onInput={(e) => {
              const html = e.currentTarget.innerHTML;
              onChange(html);
            }}
            className={cn(
              "min-h-[150px] p-3 border border-white/10 rounded bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-primary prose prose-invert max-w-none",
              disabled && "cursor-not-allowed opacity-50"
            )}
            style={{
              fontFamily: "inherit",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
            data-placeholder={placeholder}
          />
        </div>
      </div>
    </div>
  );
};

